/**
 * Drainage Hole Quantity Synchronization
 * 
 * This script automatically syncs the quantity of "Add Drainage Hole" items
 * with their linked product quantities in the cart.
 */

class DrainageHoleSync {
  constructor() {
    this.cartUpdateUnsubscriber = undefined;
    this.init();
  }

  init() {
    // Subscribe to cart update events
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Skip if this sync triggered the update to avoid infinite loops
      if (event.source === 'drainage-hole-sync') {
        return;
      }
      
      console.log('🔄 DrainageHoleSync: Cart updated, checking for drainage hole sync needs');
      this.syncDrainageHoles(event.cartData);
    });

    // Also run initial sync on page load
    this.syncDrainageHolesOnLoad();
  }

  /**
   * Initial sync when page loads - gets cart data via fetch
   */
  async syncDrainageHolesOnLoad() {
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      const cartData = await response.json();
      console.log('🔄 DrainageHoleSync: Initial page load sync');
      this.syncDrainageHoles(cartData);
    } catch (error) {
      console.error('🚨 DrainageHoleSync: Failed to fetch cart data on load:', error);
    }
  }

  /**
   * Main sync logic - ensures drainage addon quantity matches total products with drainage
   *
   * NEW CONSOLIDATED MODEL:
   * - There should be only ONE drainage addon line item in cart
   * - Its quantity should equal the TOTAL quantity of all products with "Add Hole for Drainage" = "Yes"
   */
  syncDrainageHoles(cartData) {
    if (!cartData || !cartData.items) {
      console.log('🔄 DrainageHoleSync: No cart data available');
      return;
    }

    console.log('🔄 DrainageHoleSync: Analyzing cart:', cartData.items.length, 'items');

    // Find drainage addon and calculate total products with drainage
    let drainageItem = null;
    let drainageLineNumber = 0;
    let totalProductsWithDrainage = 0;

    cartData.items.forEach((item, index) => {
      const lineNumber = index + 1;

      if (this.isDrainageHoleItem(item)) {
        // Found drainage addon
        if (!drainageItem) {
          drainageItem = item;
          drainageLineNumber = lineNumber;
          console.log(`💧 Found drainage addon: ${item.product_title} (line ${lineNumber}, qty: ${item.quantity})`);
        } else {
          // Multiple drainage items found - this shouldn't happen but log it
          console.warn(`⚠️ Multiple drainage items found! Line ${lineNumber} should be consolidated.`);
        }
      } else if (item.properties && item.properties['Add Hole for Drainage'] === 'Yes') {
        // Product with drainage selected
        totalProductsWithDrainage += item.quantity;
        console.log(`🔧 Product with drainage: ${item.product_title} (qty: ${item.quantity})`);
      }
    });

    console.log(`📊 DrainageHoleSync: Total products with drainage: ${totalProductsWithDrainage}`);

    const syncUpdates = [];

    // Case 1: Drainage addon exists but no products with drainage - remove it
    if (drainageItem && totalProductsWithDrainage === 0) {
      console.log(`🗑️ DrainageHoleSync: No products with drainage, removing drainage addon`);
      syncUpdates.push({
        line: drainageLineNumber,
        quantity: 0,
        reason: 'No products with drainage in cart'
      });
    }
    // Case 2: Drainage addon exists and quantities don't match - update it
    else if (drainageItem && drainageItem.quantity !== totalProductsWithDrainage) {
      console.log(`🔄 DrainageHoleSync: Quantity mismatch - drainage: ${drainageItem.quantity}, products with drainage: ${totalProductsWithDrainage}`);
      syncUpdates.push({
        line: drainageLineNumber,
        quantity: totalProductsWithDrainage,
        reason: `Syncing with total products with drainage`
      });
    }
    // Case 3: Quantities match - no update needed
    else if (drainageItem) {
      console.log(`✅ DrainageHoleSync: Quantities match (${drainageItem.quantity})`);
    }
    // Case 4: No drainage addon but products with drainage exist - shouldn't happen, but log it
    else if (totalProductsWithDrainage > 0) {
      console.warn(`⚠️ DrainageHoleSync: Products with drainage exist but no drainage addon found!`);
    }

    // Apply sync updates
    if (syncUpdates.length > 0) {
      console.log('🔄 DrainageHoleSync: Applying updates:', syncUpdates);
      this.applySyncUpdates(syncUpdates);
    } else {
      console.log('✅ DrainageHoleSync: No sync updates needed');
    }
  }

  /**
   * Check if an item is a drainage hole addon
   */
  isDrainageHoleItem(item) {
    return item.sku === 'ADH' ||
           (item.properties && item.properties.addon_type === 'drainage_hole');
  }


  /**
   * Apply quantity sync updates to cart
   */
  async applySyncUpdates(updates) {
    for (const update of updates) {
      console.log(`🔄 DrainageHoleSync: Updating line ${update.line} to quantity ${update.quantity} - ${update.reason}`);
      
      try {
        const sectionsToUpdate = ['main-cart-items', 'cart-icon-bubble', 'cart-live-region-text', 'main-cart-footer'].filter(section => {
          const element = document.querySelector(`[data-id="${section}"], #${section}`);
          console.log(`🔍 DrainageHoleSync: Checking section "${section}": ${element ? 'FOUND' : 'NOT FOUND'}`);
          return element;
        });
        
        console.log(`📤 DrainageHoleSync: Requesting updates for sections:`, sectionsToUpdate);
        
        const body = JSON.stringify({
          line: update.line,
          quantity: update.quantity,
          sections: sectionsToUpdate,
          sections_url: window.location.pathname,
        });

        const response = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body: body
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.text();
        const parsedResult = JSON.parse(result);

        if (parsedResult.errors) {
          console.error('🚨 DrainageHoleSync: Cart update failed:', parsedResult.errors);
          continue;
        }

        console.log(`✅ DrainageHoleSync: Successfully updated line ${update.line}`);
        console.log(`📥 DrainageHoleSync: API response sections:`, Object.keys(parsedResult.sections || {}));
        
        // Check if main-cart-footer section was returned
        if (parsedResult.sections && parsedResult.sections['main-cart-footer']) {
          console.log(`💰 DrainageHoleSync: Cart footer section was updated`);
          // Manually recalculate wholesale total using updated cart data
          this.updateWholesaleTotal(parsedResult);
        } else {
          console.log(`⚠️ DrainageHoleSync: Cart footer section was NOT returned in response`);
        }
        
        // Publish cart update event so other components can react, but mark it as from our sync
        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: 'drainage-hole-sync', 
          cartData: parsedResult 
        });

      } catch (error) {
        console.error(`🚨 DrainageHoleSync: Failed to update line ${update.line}:`, error);
      }

      // Small delay between updates to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /**
   * Update the wholesale total display using JavaScript
   */
  updateWholesaleTotal(cartData) {
    try {
      // Check if customer is wholesale
      if (!window.customer || !window.customer.tags || !window.customer.tags.includes('wholesale')) {
        console.log('💰 DrainageHoleSync: Not wholesale customer, skipping total update');
        return;
      }

      let wholesaleTotal = 0;
      
      // Calculate wholesale total using same logic as Liquid template
      cartData.items.forEach(item => {
        // Check if item has disc_price by looking at the cart item element
        const cartItemElement = document.getElementById(`CartItem-${cartData.items.indexOf(item) + 1}`);
        if (cartItemElement && cartItemElement.dataset.discPrice) {
          // Use wholesale price
          const discPrice = parseFloat(cartItemElement.dataset.discPrice) * 100; // Convert to cents
          const itemTotal = discPrice * item.quantity;
          wholesaleTotal += itemTotal;
          console.log(`💰 Using wholesale price for ${item.product_title}: $${discPrice/100} × ${item.quantity} = $${itemTotal/100}`);
        } else {
          // Use retail price as fallback
          wholesaleTotal += item.final_line_price;
          console.log(`💰 Using retail price for ${item.product_title}: $${item.final_line_price/100}`);
        }
      });

      console.log(`💰 DrainageHoleSync: Calculated wholesale total: $${wholesaleTotal/100}`);

      // Update the total display
      const totalValueElement = document.querySelector('.totals__total-value');
      if (totalValueElement) {
        // Format the price (assuming USD)
        const formattedPrice = (wholesaleTotal / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD'
        });
        totalValueElement.textContent = formattedPrice;
        console.log(`💰 DrainageHoleSync: Updated total display to ${formattedPrice}`);
      } else {
        console.log('⚠️ DrainageHoleSync: Could not find total display element');
      }

    } catch (error) {
      console.error('🚨 DrainageHoleSync: Error updating wholesale total:', error);
    }
  }

  /**
   * Manual sync function for debugging - can be called from browser console
   */
  async testSync() {
    console.log('🧪 DrainageHoleSync: Manual test sync initiated');
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      const cartData = await response.json();
      console.log('🧪 Current cart data:', cartData);
      this.syncDrainageHoles(cartData);
    } catch (error) {
      console.error('🚨 DrainageHoleSync: Test sync failed:', error);
    }
  }

  /**
   * Cleanup when component is destroyed
   */
  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
}

// Initialize drainage hole sync when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  console.log('🔄 DrainageHoleSync: DOM loaded, checking dependencies...');
  
  // Check if required dependencies are available
  if (typeof PUB_SUB_EVENTS === 'undefined') {
    console.error('🚨 DrainageHoleSync: PUB_SUB_EVENTS not defined - constants.js not loaded yet');
    return;
  }
  
  if (typeof subscribe === 'undefined') {
    console.error('🚨 DrainageHoleSync: subscribe not defined - pubsub.js not loaded yet');
    return;
  }
  
  if (typeof fetchConfig === 'undefined') {
    console.error('🚨 DrainageHoleSync: fetchConfig not defined - global.js not loaded yet');
    return;
  }
  
  if (typeof routes === 'undefined') {
    console.error('🚨 DrainageHoleSync: routes not defined');
    return;
  }
  
  console.log('🔄 DrainageHoleSync: All dependencies available, initializing...');
  window.drainageHoleSync = new DrainageHoleSync();
});

// Also try to initialize on window load as fallback
window.addEventListener('load', () => {
  if (!window.drainageHoleSync) {
    console.log('🔄 DrainageHoleSync: Fallback initialization on window load');
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe !== 'undefined' && typeof fetchConfig !== 'undefined') {
      window.drainageHoleSync = new DrainageHoleSync();
    } else {
      console.error('🚨 DrainageHoleSync: Dependencies still not available on window load');
    }
  }
});